//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { unitAPI } from "./unitAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type UnitDetails,
  type CreateUnitsInput,
  type UpdateUnitInput,
} from "./types/types";

/**
 * Custom hook `useUnits` to manage operations related to units, such as fetching, creating, updating, and deleting.
 * Also manages pagination and search functionality.
 *
 * @param {string} token - Authentication token for API requests.
 * @returns {Object} The hook returns unit data, loading state, error messages, and functions to interact with the API.
 */
export const useUnits = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "units",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      unitAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const units = data?.units || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createUnitMutation = useMutation({
    mutationFn: (unitData: CreateUnitsInput) =>
      unitAPI.create(
        { ...unitData, description: unitData.description || "" },
        token
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
    },
  });

  const updateUnitMutation = useMutation({
    mutationFn: ({ id, ...unitData }: UpdateUnitInput) =>
      unitAPI.update(
        id,
        { ...unitData, description: unitData.description || "" },
        token
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
    },
  });

  const deleteUnitMutation = useMutation({
    mutationFn: (id: string) => unitAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
    },
  });

  const getUnitById = useCallback(
    async (
      id: string,
      options: {
        searchTermStudents?: string;
        studentFilter?: "all" | "active" | "inactive";
        pageStudents?: number;
        perPageStudents?: number;
        searchTermTeams?: string;
        pageTeams?: number;
        perPageTeams?: number;
      } = {}
    ): Promise<UnitDetails | null> => {
      try {
        const unitDetails = (await unitAPI.getById(
          id,
          token,
          options
        )) as UnitDetails;
        return unitDetails;
      } catch (err) {
        console.error("Error fetching unit by ID:", err);
        return null;
      }
    },
    [token]
  );

  return {
    units,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createUnit: createUnitMutation.mutate,
    updateUnit: updateUnitMutation.mutate,
    deleteUnit: deleteUnitMutation.mutate,
    getUnitById,
    changePage,
    isCreating: createUnitMutation.isPending,
    isUpdating: updateUnitMutation.isPending,
    isDeleting: deleteUnitMutation.isPending,
  };
};
